import React, { useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'

/**
 * AuthDebugger Component
 * 
 * This component provides real-time debugging information about the authentication state.
 * It logs all state changes and helps identify where the authentication flow is failing.
 */
export const AuthDebugger: React.FC = () => {
  const authState = useAuthStore()

  useEffect(() => {
    console.log('🔍 AuthDebugger: Auth state changed:', {
      timestamp: new Date().toISOString(),
      isAuthenticated: authState.isAuthenticated,
      hasUser: !!authState.user,
      hasToken: !!authState.token,
      isLoading: authState.isLoading,
      error: authState.error,
      userDetails: authState.user ? {
        id: authState.user.id,
        email: authState.user.email,
        username: authState.user.username,
        role: authState.user.role,
        mfa_enabled: authState.user.mfa_enabled
      } : null,
      tokenLength: authState.token?.length,
      storeState: {
        isAuthenticated: authState.isAuthenticated,
        hasUser: !!authState.user,
        hasToken: !!authState.token
      }
    })

    // Critical check: If we have user and token but not authenticated
    if (authState.user && authState.token && !authState.isAuthenticated) {
      console.error('🚨 CRITICAL AUTH BUG: Have user and token but isAuthenticated is false!', {
        user: authState.user,
        tokenPreview: authState.token.substring(0, 20) + '...',
        isAuthenticated: authState.isAuthenticated
      })
    }

    // Check if we're in a stuck state
    if (authState.isLoading && !authState.error) {
      console.warn('⚠️ AUTH WARNING: Loading state without error - possible stuck state')
    }

  }, [authState.isAuthenticated, authState.user, authState.token, authState.isLoading, authState.error])

  // Only render in development
  if (process.env.NODE_ENV !== 'development') {
    return null
  }

  return (
    <div style={{
      position: 'fixed',
      top: 10,
      right: 10,
      background: 'rgba(0,0,0,0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      zIndex: 9999,
      maxWidth: '300px',
      fontFamily: 'monospace'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>🔍 Auth Debug</div>
      <div>Authenticated: {authState.isAuthenticated ? '✅' : '❌'}</div>
      <div>User: {authState.user ? '✅' : '❌'}</div>
      <div>Token: {authState.token ? '✅' : '❌'}</div>
      <div>Loading: {authState.isLoading ? '⏳' : '✅'}</div>
      <div>Error: {authState.error ? '❌' : '✅'}</div>
      {authState.user && (
        <div style={{ marginTop: '5px', fontSize: '10px' }}>
          <div>Email: {authState.user.email}</div>
          <div>Role: {authState.user.role}</div>
        </div>
      )}
      {authState.error && (
        <div style={{ marginTop: '5px', color: 'red', fontSize: '10px' }}>
          Error: {authState.error}
        </div>
      )}
    </div>
  )
}

export default AuthDebugger